import { ChangeEvent, useEffect, useState } from "react";
import { LABELS } from "~/resources/Labels";
import { SIMPLTable } from "../../components/commons/table/SimplTable";
import { Layout } from "../../components/commons/layout/Layout";
import AddIcon from "@mui/icons-material/Add";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "../../components/commons/dialog/SimplAlert";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { Entries } from "~/models/enums/Entries";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { useTranslation } from "react-i18next";
import { PropRow, SortTable } from "~/models/Commons ";
import { Alerts } from "../messages/Alerts";
import AddOutputTemplate from "./AddOutPutTemplate";
import { DetailViewOutputTemplate } from "./DetailViewOutputTemplate";
import { OutPutTemplateHeader } from "~/models/OutPutTemplateHeader";
import { OutputTemplateItem } from "~/models/outputTemplate/OutputTemplate";
import {
  useGetOutputTemplatesQuery,
  useDeleteOutputTemplateMutation,
} from "~/services/outputTemplate";

export const OutputTemplate = () => {
  const { t } = useTranslation();

  const [isAddOutputTemplateModalOpen, setIsAddOutputTemplateModalOpen] = useState(false);
  const [showOutputTemplateFilter, setShowOutputTemplateFilter] = useState<boolean>(false);
  const [outputTemplateRows, setOutputTemplateRows] = useState<PropRow[]>([]);
  const [selectedOutputTemplate, setSelectedOutputTemplate] = useState<OutputTemplateItem | null>(null);
  const [isDeleteOutputTemplateModalOpen, setIsDeleteOutputTemplateModalOpen] = useState(false);
  const [isOutputTemplateDetailModalOpen, setIsOutputTemplateDetailModalOpen] = useState(false);
  const [isOutputTemplateActive, setIsOutputTemplateActive] = useState(true);

  const [outputTemplatePageSize, setOutputTemplatePageSize] = useState<number>(5);
  const [outputTemplatePage, setOutputTemplatePage] = useState<number>(0);
  const [showOutputTemplatePagination, setShowOutputTemplatePagination] = useState(true);

  const [showOutputTemplateSuccessModal, setShowOutputTemplateSuccessModal] = useState(false);
  const [showOutputTemplateErrorModal, setShowOutputTemplateErrorModal] = useState(false);

  type ResponseDetails = { title: string; message: string };
  const [responseDetails, setResponseDetails] = useState<ResponseDetails | null>(null);

  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });

  const { data, refetch, isLoading, isFetching, isError } = useGetOutputTemplatesQuery({
    page: 0,
    size: 9999,
    active: isOutputTemplateActive,
  });

  const [deleteOutputTemplate] = useDeleteOutputTemplateMutation();

  const initOutputTemplateData = () => {
    const payload = data?.payload;
    if (!payload?.content || payload.content.length === 0) {
      setShowOutputTemplatePagination(false);
      setOutputTemplateRows([]);
      return;
    }

    const content = [...payload.content];

    if (sortConfig.key) {
      content.sort((a: OutputTemplateItem, b: OutputTemplateItem) => {
        const aVal = a[sortConfig.key] ?? '';
        const bVal = b[sortConfig.key] ?? '';
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === LABELS.ASC ? cmp : -cmp;
      });
    }

    const mapped: PropRow[] = content.map((item: OutputTemplateItem) => ({
      row: {
        ...item,
        scriptType: item.scriptType?.name ?? '—',
        cloudProvider: item.cloudProvider?.cloudProviderName ?? '—',
        dateAdded: item.creationDate
          ? new Date(item.creationDate).toLocaleDateString()
          : '—',
        dateAdded_raw: item.creationDate,
        _original: item,
      },
    }));

    setOutputTemplateRows(mapped);
    setShowOutputTemplatePagination(true);
  };

  useEffect(() => {
    initOutputTemplateData();
  }, [data, sortConfig, outputTemplatePageSize]);

  useEffect(() => {
    setOutputTemplatePage(0);
    handleOutputTemplatePaginationSize();
  }, [outputTemplatePageSize]);

  const handleOutputTemplatePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(outputTemplatePageSize)) {
      setOutputTemplatePage(0);
    }
  };

  const toggleOutputTemplateFilter = () => setShowOutputTemplateFilter((prev) => !prev);
  const closeAddOutputTemplateModal = () => setIsAddOutputTemplateModalOpen(false);

  const handleOutputTemplateSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setOutputTemplatePageSize(Number(e.target.value));
  };

  const resetOutputTemplateAll = () => {
    setOutputTemplatePage(0);
    setShowOutputTemplateFilter(false);
  };

  const handleOutputTemplatePageChange = (page: number) => {
    setOutputTemplatePage(page);
  };

  const handleOutputTemplateSort = (key: string) => {
    const direction = sortConfig.key === key && sortConfig.direction === LABELS.ASC
      ? LABELS.Desc
      : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  const handleConfirmDeleteOutputTemplate = (item: PropRow['row']) => {
    const original: OutputTemplateItem = item._original ?? item;
    if (original.id) {
      setSelectedOutputTemplate(original);
      setIsDeleteOutputTemplateModalOpen(true);
    }
  };

  const handleDeleteOutputTemplateItem = async () => {
    if (!selectedOutputTemplate?.id) return;
    setIsDeleteOutputTemplateModalOpen(false);
    try {
      await deleteOutputTemplate(selectedOutputTemplate.id).unwrap();
      refetch();
      setResponseDetails({
        title: t('MessageOperationCompletedSuceesuflly'),
        message: t('EntityInactivatedSuccessfully'),
      });
      setShowOutputTemplateSuccessModal(true);
      setShowOutputTemplateErrorModal(false);
    } catch (error: any) {
      setResponseDetails({
        title: `${error?.data?.title ?? t('Error')} (${error?.data?.status ?? ''})`,
        message: error?.data?.detail ?? t('UnexpectedError'),
      });
      setShowOutputTemplateErrorModal(true);
      setShowOutputTemplateSuccessModal(false);
    }
  };

  const handleOpenDetail = (item: PropRow['row']) => {
    const original: OutputTemplateItem = item._original ?? item;
    setSelectedOutputTemplate(original);
    setIsOutputTemplateDetailModalOpen(true);
  };

  const renderButtonAddOutputTemplate = () => (
    <SIMPLButtonCustomized
      label={t('AddOutputTemplate')}
      buttonHandlerAction={() => setIsAddOutputTemplateModalOpen(true)}
      secondIcon={<AddIcon fontSize="small" />}
      primary
      testId="output-template-add-button"
    />
  );

  const renderDeleteAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t('Cancel')}
          secondary
          buttonHandlerAction={() => setIsDeleteOutputTemplateModalOpen(false)}
          testId="output-template-delete-cancel"
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t('Delete')}
          danger
          buttonHandlerAction={handleDeleteOutputTemplateItem}
          testId="output-template-delete-confirm"
        />
      </div>
    </>
  );

  const renderDeleteOutputTemplateAlert = () =>
    isDeleteOutputTemplateModalOpen && selectedOutputTemplate ? (
      <SIMPLAlert
        onCancel={() => setIsDeleteOutputTemplateModalOpen(false)}
        message={t('MessageDialogDelete', { item: selectedOutputTemplate.name })}
        title={t('OutputTemplateDelete')}
        actions={renderDeleteAlertButtons()}
      />
    ) : null;

  const renderAlerts = () => (
    <Alerts
      showSuccess={showOutputTemplateSuccessModal}
      showError={showOutputTemplateErrorModal}
      title={responseDetails?.title}
      successMessage={responseDetails?.message || ""}
      errorMessage={responseDetails?.message || ""}
      onCloseSucessDialog={() => setShowOutputTemplateSuccessModal(false)}
      onCloseErrorDialog={() => setShowOutputTemplateErrorModal(false)}
      enabledSuccess={showOutputTemplateSuccessModal}
      enabledError={showOutputTemplateErrorModal}
    />
  );

  const renderOutputTemplateTable = () => (
    <SIMPLTable
      title={t('OutputTemplate')}
      custumizedNoDataAvailable={t('NoDataAvailable')}
      clear={resetOutputTemplateAll}
      page={outputTemplatePage}
      size={outputTemplatePageSize}
      showPagination={showOutputTemplatePagination}
      active={true}
      showHandler={toggleOutputTemplateFilter}
      filterActive={showOutputTemplateFilter}
      tableHeaders={OutPutTemplateHeader}
      rows={outputTemplateRows}
      onChangePage={handleOutputTemplatePageChange}
      onChange={handleOutputTemplateSizeChange}
      button={renderButtonAddOutputTemplate()}
      sortConfig={sortConfig}
      handleSort={handleOutputTemplateSort}
      defaultSortKey="dateAdded_raw"
      chipLabels={[]}
      handleDelete={handleConfirmDeleteOutputTemplate}
      handleOpenDetail={handleOpenDetail}
      showButtonDeleteInModal
      showButtonDetailsInModal
      showButtonDownloadInModal={false}
      showButtonDecommissionInModal={false}
      showEditInModal={false}
      showCopyInModal={false}
      showConfigFileInModal={false}
      showReviewOutputTemplateInModal={false}
      handlerChips={() => {}}
      setIsActive={setIsOutputTemplateActive}
      isActive={isOutputTemplateActive}
      activeLabel={t('Active')}
      inactiveLabel={t('Inactive')}
      tabs
    />
  );

  return (
    <SIMPLMenuHeader>
      {renderAlerts()}
      <Layout>
        {renderDeleteOutputTemplateAlert()}

        {isAddOutputTemplateModalOpen && (
          <AddOutputTemplate
            onCloseModal={closeAddOutputTemplateModal}
            editedItem={null}
            setConfig={setSortConfig}
            title={t('AddOutputTemplate')}
            refetch={refetch}
            onSuccess={(title, message) => {
              setResponseDetails({ title, message });
              setShowOutputTemplateSuccessModal(true);
              setShowOutputTemplateErrorModal(false);
            }}
            onError={(title, message) => {
              setResponseDetails({ title, message });
              setShowOutputTemplateErrorModal(true);
              setShowOutputTemplateSuccessModal(false);
            }}
          />
        )}

        {isOutputTemplateDetailModalOpen && selectedOutputTemplate && (
          <DetailViewOutputTemplate
            item={selectedOutputTemplate}
            onClose={() => setIsOutputTemplateDetailModalOpen(false)}
          />
        )}

        <LoadingWrapper
          loadingTitle={t('LoadingOutputTemplates')}
          isLoading={isLoading || isFetching}
          isError={isError}
        >
          {renderOutputTemplateTable()}
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
